<template>
  <section class="flacs-dashboard">
    <!-- 筛选区域 -->
    <el-card class="filter-card" :body-style="{ padding: isMobile ? '12px' : '16px' }">
      <div v-if="isMobile" class="filter-toggle" @click="filterCollapsed = !filterCollapsed">
        <i :class="filterCollapsed ? 'el-icon-arrow-down' : 'el-icon-arrow-up'"></i>
        {{ filterCollapsed ? '展开筛选条件' : '收起筛选条件' }}
      </div>
      <el-form 
        class="filter-form" 
        :model="filters" 
        :inline="!isMobile"
        :label-position="isMobile ? 'top' : 'right'"
        :label-width="isMobile ? '' : '80px'"
        size="small"
        v-show="!isMobile || !filterCollapsed"
      >
        <!-- 管理员：区域-销售选择器 -->
        <el-form-item v-if="userRole === 'Admin'" label="区域/销售">
          <el-cascader
            v-model="cascaderValue"
            :options="regionSalesOptions"
            :props="cascaderProps"
            :style="{ width: isMobile ? '100%' : '280px' }"
            clearable
            filterable
            collapse-tags
            placeholder="请选择区域/销售"
            @change="onCascaderChange"
          ></el-cascader>
        </el-form-item>

        <!-- 经理：销售下拉选择器（多选） -->
        <el-form-item v-if="userRole === 'Manager'" label="销售人员">
          <el-select
            v-model="filters.salesPostIds"
            multiple
            collapse-tags
            clearable
            filterable
            placeholder="全部下属"
            :style="{ width: isMobile ? '100%' : '200px' }"
            @change="onSalesChange"
          >
            <el-option v-for="item in salesOptions" :key="item.postId" :label="item.name" :value="item.postId"></el-option>
          </el-select>
        </el-form-item>

        <!-- 日期选择器 -->
        <el-form-item label="手术开始日期">
          <el-date-picker
            v-model="mobileStartDate"
            type="date"
            placeholder="手术开始日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :style="{ width: isMobile ? '100%' : '120px' }"
            :clearable="false"
            :picker-options="startDateOptions"
            @change="onStartDateChange"
          ></el-date-picker>
        </el-form-item>
        <el-form-item label="手术结束日期">
          <el-date-picker
            v-model="mobileEndDate"
            type="date"
            placeholder="手术结束日期"
            format="yyyy-MM-dd"
            value-format="yyyy-MM-dd"
            :style="{ width: isMobile ? '100%' : '120px' }"
            :clearable="false"
            :picker-options="endDateOptions"
            @change="onEndDateChange"
          ></el-date-picker>
        </el-form-item>

        <!-- ZCN ID 输入框 -->
        <el-form-item label="ZCN ID">
          <el-input 
            v-model="filters.zcnCode" 
            clearable 
            placeholder="输入ZCN ID" 
            :style="{ width: isMobile ? '100%' : '130px' }"
            @keyup.enter.native="fetchDashboardData"
            @clear="fetchDashboardData"
            @blur="onZcnCodeBlur"
          ></el-input>
        </el-form-item>

        <!-- 医院名称下拉 -->
        <el-form-item label="医院名称">
          <el-select
            v-model="filters.hospitalName"
            clearable
            filterable
            remote
            :remote-method="searchHospitals"
            :loading="hospitalLoading"
            placeholder="搜索医院"
            :style="{ width: isMobile ? '100%' : '180px' }"
            @change="fetchDashboardData"
          >
            <el-option v-for="item in hospitalOptions" :key="item.zcnCode" :label="item.name" :value="item.name"></el-option>
          </el-select>
        </el-form-item>

        <!-- 省份下拉 -->
        <el-form-item label="省份">
          <el-select
            v-model="filters.provinceId"
            clearable
            filterable
            placeholder="全部省份"
            :style="{ width: isMobile ? '100%' : '150px' }"
            @change="fetchDashboardData"
          >
            <el-option v-for="item in provinceOptions" :key="item.id" :label="item.name" :value="item.id"></el-option>
          </el-select>
        </el-form-item>
      </el-form>
    </el-card>

    <!-- 图表区域 -->
    <div class="charts-container">
      <!-- 达成情况图表 -->
      <div class="chart-card" ref="achievementCard">
        <div class="chart-header">
          <span class="chart-title">
            <i class="el-icon-s-data"></i>
            达成情况
            <el-tooltip effect="dark" placement="top">
              <div slot="content">
                <div>• 指标：当月按天数比例折算，其他月份为完整目标</div>
                <div>• 上报数量：选定时间范围内的填报总数</div>
                <div>• 达成率 = 上报数量 ÷ 指标 × 100%</div>
                <div>• 增长率 = (今年上报 - 去年同期上报) ÷ 去年同期上报 × 100%</div>
              </div>
              <i class="el-icon-question tip-icon"></i>
            </el-tooltip>
          </span>
          <div class="chart-actions">
            <template v-if="!isMobile">
              <el-radio-group v-model="achievementChartSort.field" size="mini" class="sort-radio-group" @change="onAchievementChartSortChange">
                <el-radio-button label="achieveRate">达成率</el-radio-button>
                <el-radio-button label="growthRate">增长率</el-radio-button>
                <el-radio-button label="actual">上报</el-radio-button>
                <el-radio-button label="target">指标</el-radio-button>
                <el-radio-button label="name">名称</el-radio-button>
              </el-radio-group>
              <el-button type="text" size="mini" :icon="achievementChartSort.order === 'asc' ? 'el-icon-sort-up' : 'el-icon-sort-down'" @click="toggleAchievementChartOrder">{{ achievementChartSort.order === 'asc' ? '升序' : '降序' }}</el-button>
            </template>
            <el-button type="text" size="mini" :icon="isFullscreen && fullscreenType === 'achievement' ? 'el-icon-close' : 'el-icon-full-screen'" @click="toggleFullscreen('achievement', $refs.achievementCard)">{{ isFullscreen && fullscreenType === 'achievement' ? '退出' : '全屏' }}</el-button>
          </div>
        </div>
        <!-- 手机端排序栏 -->
        <div v-if="isMobile" class="mobile-sort-bar">
          <el-radio-group v-model="achievementChartSort.field" size="mini" @change="onAchievementChartSortChange">
            <el-radio-button label="achieveRate">达成率</el-radio-button>
            <el-radio-button label="growthRate">增长率</el-radio-button>
            <el-radio-button label="actual">上报</el-radio-button>
            <el-radio-button label="target">指标</el-radio-button>
            <el-radio-button label="name">名称</el-radio-button>
          </el-radio-group>
          <el-button type="text" size="mini" :icon="achievementChartSort.order === 'asc' ? 'el-icon-sort-up' : 'el-icon-sort-down'" @click="toggleAchievementChartOrder"></el-button>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton">
            <div class="skeleton-bar" v-for="i in 5" :key="i" :style="{ width: (100 - i * 15) + '%' }"></div>
          </div>
          <div v-else-if="!achievementData.length" class="chart-empty">
            <i class="el-icon-data-analysis"></i>
            <p>暂无数据</p>
            <span class="empty-hint">请调整筛选条件后重试</span>
          </div>
          <div v-else class="chart-content">
            <div ref="achievementChart" class="chart-area"></div>
            <div v-if="!isFullscreen" class="table-collapse-wrapper">
              <div class="table-collapse-actions">
                <div class="table-collapse-header" @click="achievementTableExpanded = !achievementTableExpanded">
                  <i :class="achievementTableExpanded ? 'el-icon-arrow-down' : 'el-icon-arrow-right'"></i>
                  <span>{{ achievementTableExpanded ? '收起明细' : '展开明细' }}</span>
                </div>
                <el-button v-if="achievementTableExpanded && !isMobile" type="text" size="mini" icon="el-icon-download" @click="exportAchievementExcel">导出Excel</el-button>
              </div>
              <div v-show="achievementTableExpanded" class="pivot-table-wrapper">
                <table class="pivot-table sortable-table">
                  <thead>
                    <tr>
                      <th class="sortable-th" @click="sortAchievementData('name')">
                        {{ getDimensionLabel() }}
                        <i :class="getAchievementSortIcon('name')"></i>
                      </th>
                      <th class="sortable-th" @click="sortAchievementData('target')">
                        指标
                        <i :class="getAchievementSortIcon('target')"></i>
                      </th>
                      <th class="sortable-th" @click="sortAchievementData('actual')">
                        上报数量
                        <i :class="getAchievementSortIcon('actual')"></i>
                      </th>
                      <th class="sortable-th" @click="sortAchievementData('lastYearActual')">
                        去年同期上报量
                        <i :class="getAchievementSortIcon('lastYearActual')"></i>
                      </th>
                      <th class="sortable-th" @click="sortAchievementData('achieveRate')">
                        达成率
                        <i :class="getAchievementSortIcon('achieveRate')"></i>
                      </th>
                      <th class="sortable-th" @click="sortAchievementData('growthRate')">
                        增长率
                        <i :class="getAchievementSortIcon('growthRate')"></i>
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="item in sortedAchievementData" :key="item.code">
                      <td>{{ item.name }}</td>
                      <td>{{ formatNumber(item.target) }}</td>
                      <td>{{ formatNumber(item.actual) }}</td>
                      <td>{{ formatNumber(item.lastYearActual) }}</td>
                      <td><span :class="getRateClass(item.achieveRate)">{{ item.achieveRate }}%</span></td>
                      <td><span :class="getGrowthClass(item.growthRate)">{{ formatGrowthRate(item.growthRate) }}</span></td>
                    </tr>
                    <!-- 汇总行 -->
                    <tr class="summary-row">
                      <td><strong>汇总</strong></td>
                      <td><strong>{{ formatNumber(achievementSummary.target) }}</strong></td>
                      <td><strong>{{ formatNumber(achievementSummary.actual) }}</strong></td>
                      <td><strong>{{ formatNumber(achievementSummary.lastYearActual) }}</strong></td>
                      <td><strong><span :class="getRateClass(achievementSummary.achieveRate)">{{ achievementSummary.achieveRate }}%</span></strong></td>
                      <td><strong><span :class="getGrowthClass(achievementSummary.growthRate)">{{ formatGrowthRate(achievementSummary.growthRate) }}</span></strong></td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- 医院分析 - 表格化 -->
      <div class="chart-card" ref="hospitalCard">
        <div class="chart-header">
          <span class="chart-title">
            <i class="el-icon-office-building"></i>
            医院分析
          </span>
          <div class="chart-actions">
            <el-button v-if="!isMobile" type="text" size="mini" icon="el-icon-download" @click="exportHospitalExcel">导出Excel</el-button>
          </div>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton">
            <div class="skeleton-bar horizontal" v-for="i in 5" :key="i" :style="{ width: (100 - i * 15) + '%' }"></div>
          </div>
          <div v-else-if="!hospitalAnalysis.length" class="chart-empty">
            <i class="el-icon-office-building"></i>
            <p>暂无数据</p>
            <span class="empty-hint">请调整筛选条件后重试</span>
          </div>
          <div v-else class="pivot-table-wrapper" style="max-height: 450px; margin-top: 0;">
            <table class="pivot-table sortable-table">
              <thead>
                <tr>
                  <th class="sortable-th" @click="sortHospitalData('name')">
                    医院名称
                    <i :class="getHospitalSortIcon('name')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('regionName')">
                    区域
                    <i :class="getHospitalSortIcon('regionName')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('salesName')">
                    姓名
                    <i :class="getHospitalSortIcon('salesName')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('target')">
                    指标
                    <i :class="getHospitalSortIcon('target')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('actual')">
                    上报数量
                    <i :class="getHospitalSortIcon('actual')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('lastYearActual')">
                    去年同期上报量
                    <i :class="getHospitalSortIcon('lastYearActual')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('achieveRate')">
                    达成率
                    <i :class="getHospitalSortIcon('achieveRate')"></i>
                  </th>
                  <th class="sortable-th" @click="sortHospitalData('growthRate')">
                    增长率
                    <i :class="getHospitalSortIcon('growthRate')"></i>
                  </th>
                </tr>
              </thead>
              <tbody>
                <!-- 汇总行 - 置顶 -->
                <tr class="summary-row">
                  <td><strong>汇总</strong></td>
                  <td><strong>-</strong></td>
                  <td><strong>-</strong></td>
                  <td><strong>{{ formatNumber(hospitalSummary.target) }}</strong></td>
                  <td><strong>{{ formatNumber(hospitalSummary.actual) }}</strong></td>
                  <td><strong>{{ formatNumber(hospitalSummary.lastYearActual) }}</strong></td>
                  <td><strong><span :class="getRateClass(hospitalSummary.achieveRate)">{{ hospitalSummary.achieveRate }}%</span></strong></td>
                  <td><strong><span :class="getGrowthClass(hospitalSummary.growthRate)">{{ formatGrowthRate(hospitalSummary.growthRate) }}</span></strong></td>
                </tr>
                <!-- 医院明细数据 -->
                <tr v-for="(item, idx) in sortedHospitalData" :key="'h-row-'+idx">
                  <td :title="item.name">{{ item.name }}</td>
                  <td>{{ item.regionName || '-' }}</td>
                  <td>{{ item.salesName || '-' }}</td>
                  <td>{{ formatNumber(item.target) }}</td>
                  <td>{{ formatNumber(item.actual) }}</td>
                  <td>{{ formatNumber(item.lastYearActual) }}</td>
                  <td><span :class="getRateClass(item.achieveRate)">{{ item.achieveRate }}%</span></td>
                  <td><span :class="getGrowthClass(item.growthRate)">{{ formatGrowthRate(item.growthRate) }}</span></td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <!-- 周期数量图表 - 已删除 -->

      <!-- 每日分析 -->
      <div class="chart-card" ref="dailyCard">
        <div class="chart-header">
          <span class="chart-title">
            <i class="el-icon-s-marketing"></i>
            每日分析
          </span>
          <div class="chart-actions">
            <el-button type="text" size="mini" :icon="isFullscreen && fullscreenType === 'daily' ? 'el-icon-close' : 'el-icon-full-screen'" @click="toggleFullscreen('daily', $refs.dailyCard)">{{ isFullscreen && fullscreenType === 'daily' ? '退出' : '全屏' }}</el-button>
          </div>
        </div>
        <div class="chart-body">
          <div v-if="loading" class="chart-skeleton chart-skeleton-line"></div>
          <div v-else-if="!dailyTrend.length" class="chart-empty">
            <i class="el-icon-s-data"></i>
            <p>暂无数据</p>
            <span class="empty-hint">请调整筛选条件后重试</span>
          </div>
          <div v-else>
            <!-- 图表 -->
            <div ref="dailyChart" class="chart-area chart-area-full"></div>

            <!-- 每日分析表格 -->
            <div class="analysis-table-wrapper">
              <h4 class="table-title">每日分析</h4>
              <div class="analysis-table-scroll">
                <table class="analysis-table">
                  <thead>
                    <tr>
                      <th>时间周期</th>
                      <th>上报数量</th>
                      <th>周期天数（自然日）</th>
                      <th>日均报量</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr>
                      <td>{{ dailyStatistics.period }}</td>
                      <td>{{ formatNumber(dailyStatistics.total) }}</td>
                      <td>{{ dailyStatistics.days }}</td>
                      <td>{{ dailyStatistics.average }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>

            <!-- 月度分析表格 -->
            <div class="analysis-table-wrapper">
              <h4 class="table-title">月度分析</h4>
              <div class="analysis-table-scroll">
                <table class="analysis-table">
                  <thead>
                    <tr>
                      <th>月度</th>
                      <th>上报数量</th>
                      <th>周期天数（自然日）</th>
                      <th>日均报量</th>
                      <th>增长率</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="(item, idx) in monthlyStatistics" :key="idx">
                      <td>{{ item.month }}</td>
                      <td>{{ formatNumber(item.total) }}</td>
                      <td>{{ item.days }}</td>
                      <td>{{ item.average }}</td>
                      <td :class="getGrowthClass(item.growthRate)">{{ formatGrowthRate(item.growthRate) }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </section>
</template>


<script>
import * as echarts from 'echarts'
import XLSX from 'xlsx-js-style'
import {
  flacs_dashboard_getdata,
  flacs_dashboard_getregionsalescascader,
  flacs_dashboard_getsalesoptions,
  flacs_dashboard_gethospitaloptions,
  flacs_dashboard_getprovinceoptions
} from '../../api/api'

export default {
  name: 'FlacsDashboard',
  data() {
    return {
      isMobile: false,
      filterCollapsed: false,
      loading: false,
      hospitalLoading: false,
      userRole: 'Sales',
      currentDimension: 'sales',  // 当前维度: region/sales/hospital
      // 级联选择器配置
      cascaderProps: {
        multiple: true,
        checkStrictly: false,  // 让 el-cascader 自动处理父子关系
        emitPath: true,
        value: 'value',
        label: 'label',
        children: 'children'
      },
      // 筛选条件
      filters: {
        regionIds: [],
        salesPostIds: [],
        zcnCode: '',
        hospitalName: '',
        provinceId: ''
      },
      dateRange: [],
      mobileStartDate: '',
      mobileEndDate: '',
      cascaderValue: [],
      // 日期快捷选项
      datePickerOptions: {
        shortcuts: [{
          text: '本周',
          onClick(picker) {
            const now = new Date()
            const day = now.getDay() || 7
            const start = new Date(now.getFullYear(), now.getMonth(), now.getDate() - day + 1)
            picker.$emit('pick', [start, now])
          }
        }, {
          text: '本月',
          onClick(picker) {
            const now = new Date()
            const start = new Date(now.getFullYear(), now.getMonth(), 1)
            picker.$emit('pick', [start, now])
          }
        }, {
          text: '上月',
          onClick(picker) {
            const now = new Date()
            const start = new Date(now.getFullYear(), now.getMonth() - 1, 1)
            const end = new Date(now.getFullYear(), now.getMonth(), 0)
            picker.$emit('pick', [start, end])
          }
        }, {
          text: '本季度',
          onClick(picker) {
            const now = new Date()
            const quarter = Math.floor(now.getMonth() / 3)
            const start = new Date(now.getFullYear(), quarter * 3, 1)
            picker.$emit('pick', [start, now])
          }
        }]
      },
      // 下拉选项
      regionSalesOptions: [],
      salesOptions: [],
      hospitalOptions: [],
      provinceOptions: [],
      // 图表数据
      achievementData: [],
      hospitalAnalysis: [],
      dailyTrend: [],
      // 统计数据（从后端获取，存储在 data 中）
      dailyStatisticsData: {
        period: '',
        total: 0,
        days: 0,
        average: 0
      },
      monthlyStatisticsData: [],
      // 图表实例
      charts: {
        achievement: null,
        daily: null
      },
      // 全屏状态
      isFullscreen: false,
      fullscreenType: '',
      // 上次查询的 ZCN ID（用于失焦时判断是否需要重新查询）
      lastZcnCode: '',
      // 表格折叠状态
      achievementTableExpanded: true,
      // 排序状态
      achievementSort: { field: 'achieveRate', order: 'desc' },
      hospitalSort: { field: 'achieveRate', order: 'desc' },
      // 达成情况图表排序
      achievementChartSort: { field: 'achieveRate', order: 'desc' }
    }
  },
  computed: {
    // 达成情况汇总
    achievementSummary() {
      if (!this.achievementData.length) return { target: 0, actual: 0, achieveRate: 0, lastYearActual: 0, growthRate: 0 }
      const target = this.achievementData.reduce((sum, item) => sum + item.target, 0)
      const actual = this.achievementData.reduce((sum, item) => sum + item.actual, 0)
      const lastYearActual = this.achievementData.reduce((sum, item) => sum + (item.lastYearActual || 0), 0)
      // 达成率计算：指标>0 正常计算；指标=0且实际>0 返回100%；指标=0且实际=0 返回0%
      let achieveRate = 0
      if (target > 0) {
        achieveRate = Math.round(actual / target * 1000) / 10
      } else if (actual > 0) {
        achieveRate = 100
      }
      // 增长率计算
      let growthRate = null
      if (lastYearActual > 0) {
        growthRate = Math.round((actual - lastYearActual) / lastYearActual * 1000) / 10
      } else if (actual > 0) {
        growthRate = null // 新增
      } else {
        growthRate = 0
      }
      return { target, actual, achieveRate, lastYearActual, growthRate }
    },
    // 医院分析汇总
    hospitalSummary() {
      if (!this.hospitalAnalysis.length) return { target: 0, actual: 0, achieveRate: 0, lastYearActual: 0, growthRate: 0 }
      const target = this.hospitalAnalysis.reduce((sum, item) => sum + item.target, 0)
      const actual = this.hospitalAnalysis.reduce((sum, item) => sum + item.actual, 0)
      const lastYearActual = this.hospitalAnalysis.reduce((sum, item) => sum + (item.lastYearActual || 0), 0)
      // 达成率计算：指标>0 正常计算；指标=0且实际>0 返回100%；指标=0且实际=0 返回0%
      let achieveRate = 0
      if (target > 0) {
        achieveRate = Math.round(actual / target * 1000) / 10
      } else if (actual > 0) {
        achieveRate = 100
      }
      // 增长率计算
      let growthRate = null
      if (lastYearActual > 0) {
        growthRate = Math.round((actual - lastYearActual) / lastYearActual * 1000) / 10
      } else if (actual > 0) {
        growthRate = null
      } else {
        growthRate = 0
      }
      return { target, actual, achieveRate, lastYearActual, growthRate }
    },
    // 日期趋势统计（从后端获取，通过 computed 暴露）
    dailyStatistics() {
      return this.dailyStatisticsData
    },
    // 月度分析统计（从后端获取，通过 computed 暴露）
    monthlyStatistics() {
      return this.monthlyStatisticsData
    },
    // 排序后的达成情况数据
    sortedAchievementData() {
      if (!this.achievementData.length) return []
      const data = [...this.achievementData]
      const { field, order } = this.achievementSort
      data.sort((a, b) => {
        let valA = a[field]
        let valB = b[field]
        // null 值排到最后
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1
        if (typeof valA === 'string') {
          return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
        }
        return order === 'asc' ? valA - valB : valB - valA
      })
      return data
    },
    // 排序后的医院分析数据
    sortedHospitalData() {
      if (!this.hospitalAnalysis.length) return []
      const data = [...this.hospitalAnalysis]
      const { field, order } = this.hospitalSort
      data.sort((a, b) => {
        let valA = a[field]
        let valB = b[field]
        // null 值排到最后
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1
        if (typeof valA === 'string') {
          return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
        }
        return order === 'asc' ? valA - valB : valB - valA
      })
      return data
    },
    // 开始日期选择限制：不能晚于结束日期，不能选择明天及以后
    startDateOptions() {
      const self = this
      return {
        disabledDate(time) {
          // 禁用明天及以后的日期
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          if (time.getTime() > today.getTime()) {
            return true
          }
          
          // 禁用晚于结束日期的日期
          if (self.mobileEndDate) {
            return time.getTime() > new Date(self.mobileEndDate).getTime()
          }
          return false
        }
      }
    },
    // 结束日期选择限制：不能早于开始日期，不能选择明天及以后
    endDateOptions() {
      const self = this
      return {
        disabledDate(time) {
          // 禁用明天及以后的日期
          const today = new Date()
          today.setHours(0, 0, 0, 0)
          if (time.getTime() > today.getTime()) {
            return true
          }
          
          // 禁用早于开始日期的日期
          if (self.mobileStartDate) {
            const startDate = new Date(self.mobileStartDate)
            startDate.setHours(0, 0, 0, 0)
            const compareDate = new Date(time)
            compareDate.setHours(0, 0, 0, 0)
            return compareDate.getTime() < startDate.getTime()
          }
          return false
        }
      }
    }
  },
  mounted() {
    this.checkMobile()
    window.addEventListener('resize', this.handleResize)
    document.addEventListener('fullscreenchange', this.onFullscreenChange)
    document.addEventListener('webkitfullscreenchange', this.onFullscreenChange)
    document.addEventListener('mozfullscreenchange', this.onFullscreenChange)
    document.addEventListener('MSFullscreenChange', this.onFullscreenChange)
    this.initDateRange()
    this.fetchProvinceOptions()
    this.fetchDashboardData()
  },
  beforeDestroy() {
    window.removeEventListener('resize', this.handleResize)
    document.removeEventListener('fullscreenchange', this.onFullscreenChange)
    document.removeEventListener('webkitfullscreenchange', this.onFullscreenChange)
    document.removeEventListener('mozfullscreenchange', this.onFullscreenChange)
    document.removeEventListener('MSFullscreenChange', this.onFullscreenChange)
    // 先销毁图表，再清理引用
    this.disposeCharts()
    // 清理 DOM 引用
    this.$refs.achievementChart = null
    this.$refs.dailyChart = null
  },
  methods: {
    checkMobile() {
      this.isMobile = window.innerWidth <= 767
    },
    handleResize() {
      this.checkMobile()
      this.$nextTick(() => {
        this.resizeCharts()
      })
    },
    initDateRange() {
      const now = new Date()
      const year = now.getFullYear()
      const month = now.getMonth()
      const startDate = new Date(year, month, 1)
      const startStr = this.formatDate(startDate)
      
      const endStr = this.formatDate(now)
      
      this.dateRange = [startStr, endStr]
      this.mobileStartDate = startStr
      this.mobileEndDate = endStr
    },
    formatDate(date) {
      const y = date.getFullYear()
      const m = String(date.getMonth() + 1).padStart(2, '0')
      const d = String(date.getDate()).padStart(2, '0')
      return `${y}-${m}-${d}`
    },
    formatNumber(val) {
      if (val == null || val === '') return '-'
      return Number(val).toLocaleString()
    },
    getRateClass(rate) {
      if (rate >= 100) return 'rate-success'
      if (rate >= 80) return 'rate-warning'
      return 'rate-danger'
    },
    formatGrowthRate(rate) {
      if (rate === null || rate === undefined) return '-'
      if (rate > 0) return rate + '%'
      if (rate < 0) return rate + '%'
      return '0%'
    },
    getGrowthClass(rate) {
      // 增长率仅在小于 0% 时标红，其余不标颜色
      if (rate === null || rate === undefined) return ''
      return rate < 0 ? 'rate-danger' : ''
    },
    resetFilters() {
      this.filters = { regionIds: [], salesPostIds: [], zcnCode: '', hospitalName: '', provinceId: '' }
      this.cascaderValue = []
      this.hospitalOptions = []
      this.initDateRange()
      this.fetchDashboardData()
    },
    // 获取看板数据
    getDimensionLabel() {
      const labels = {
        'region': '区域',
        'sales': '销售人员',
        'hospital': '医院名称'
      }
      return labels[this.currentDimension] || '名称'
    },
    async fetchDashboardData() {
      this.loading = true
      try {
        const params = {
          startDate: this.mobileStartDate || '',
          endDate: this.mobileEndDate || '',
          regionIds: this.filters.regionIds,
          salesPostIds: this.filters.salesPostIds,
          zcnCode: this.filters.zcnCode,
          hospitalName: this.filters.hospitalName,
          provinceId: this.filters.provinceId || ''
        }
        const res = await flacs_dashboard_getdata(params)
        const data = (res.data && res.data.response) || res.data || res.response || {}
        this.userRole = data.userRole || 'Sales'
        this.currentDimension = data.currentDimension || 'sales'  // 保存当前维度
        this.achievementData = data.achievementData || []
        this.hospitalAnalysis = data.hospitalAnalysis || []
        this.dailyTrend = data.dailyTrend || []
        
        // 从后端获取统计数据，存储到 data 中
        this.dailyStatisticsData = data.dailyStatistics || {
          period: '',
          total: 0,
          days: 0,
          average: 0
        }
        this.monthlyStatisticsData = data.monthlyStatistics || []
        
        // 首次加载时获取筛选器选项
        if (this.userRole === 'Admin' && !this.regionSalesOptions.length) {
          this.fetchRegionSalesCascader()
        } else if (this.userRole === 'Manager' && !this.salesOptions.length) {
          // 先获取销售列表（会自动全选），然后重新查询数据
          await this.fetchSalesOptions()
          // 如果自动全选了销售，重新查询数据
          if (this.filters.salesPostIds.length > 0) {
            // 递归调用自己重新查询（此时已有 salesPostIds）
            return this.fetchDashboardData()
          }
        }
        this.loading = false
        this.$nextTick(() => {
          this.renderCharts()
        })
      } catch (e) {
        console.error('获取数据失败', e)
        this.$message.error('获取数据失败，请稍后重试')
        this.loading = false
      }
    },
    // 获取区域-销售级联数据
    async fetchRegionSalesCascader() {
      try {
        const res = await flacs_dashboard_getregionsalescascader()
        var data = []
        if (Array.isArray(res.data)) {
          data = res.data
        } else if (res.data && res.data.response) {
          data = res.data.response
        } else if (res.response) {
          data = res.response
        }
        if (Array.isArray(data)) {
          this.regionSalesOptions = data.map(region => ({
            value: region.value,
            label: region.label,
            children: (region.children || []).map(sales => ({
              value: sales.value,
              label: sales.label
            }))
          }))
        }
      } catch (e) {
        console.error('获取区域销售数据失败', e)
      }
    },
    // 获取销售列表（不触发数据查询）
    async fetchSalesOptions() {
      try {
        const res = await flacs_dashboard_getsalesoptions()
        if (res.data) {
          this.salesOptions = res.data
          // 经理角色默认全选所有销售
          if (this.userRole === 'Manager' && this.salesOptions.length > 0) {
            this.filters.salesPostIds = this.salesOptions.map(s => s.postId)
          }
        }
      } catch (e) {
        console.error('获取销售列表失败', e)
      }
    },
    // 搜索医院
    async searchHospitals(keyword) {
      if (!keyword || keyword.length < 2) {
        this.hospitalOptions = []
        return
      }
      this.hospitalLoading = true
      try {
        const res = await flacs_dashboard_gethospitaloptions({
          keyword: keyword
        })
        if (res.data) {
          this.hospitalOptions = res.data
        }
      } catch (e) {
        console.error('搜索医院失败', e)
      } finally {
        this.hospitalLoading = false
      }
    },
    // 获取省份选项
    async fetchProvinceOptions() {
      try {
        const res = await flacs_dashboard_getprovinceoptions()
        if (res.data) {
          this.provinceOptions = Array.isArray(res.data) ? res.data : (res.data.response || [])
        }
      } catch (e) {
        console.error('获取省份列表失败', e)
      }
    },
    /**
     * 级联选择器变化处理
     * el-cascader 在 checkStrictly: false 模式下会自动处理父子关系
     * 我们只需要提取叶子节点（人员ID）传给后端
     */
    onCascaderChange(val) {
      // 清空所有选择
      if (!val || val.length === 0) {
        this.filters.salesPostIds = []
        this.hospitalOptions = []
        this.filters.hospitalName = ''
        this.fetchDashboardData()
        return
      }

      // 提取所有叶子节点（人员ID）
      // el-cascader 返回格式：[[regionId, salesId], [regionId, salesId], ...]
      const salesIds = val
        .filter(item => item.length === 2)  // 只要二级节点（人员）
        .map(item => item[1])  // 提取人员ID

      // 更新筛选条件
      this.filters.salesPostIds = salesIds
      this.filters.regionIds = []  // 不传大区ID给后端

      // 清空医院筛选
      this.hospitalOptions = []
      this.filters.hospitalName = ''

      // 触发数据查询
      this.fetchDashboardData()
    },
    // 销售选择变化
    onSalesChange() {
      this.hospitalOptions = []
      this.filters.hospitalName = ''
      this.fetchDashboardData()
    },
    // 开始日期变化
    onStartDateChange(val) {
      // 如果开始日期晚于结束日期，自动调整结束日期
      if (val && this.mobileEndDate && val > this.mobileEndDate) {
        this.mobileEndDate = val
      }
      
      // 确保结束日期不超过今天
      const today = new Date()
      const todayStr = this.formatDate(today)
      if (this.mobileEndDate > todayStr) {
        this.mobileEndDate = todayStr
      }
      
      this.fetchDashboardData()
    },
    // 结束日期变化
    onEndDateChange(val) {
      // 如果结束日期早于开始日期，自动调整开始日期
      if (val && this.mobileStartDate && val < this.mobileStartDate) {
        this.mobileStartDate = val
      }
      
      // 确保结束日期不超过今天
      const today = new Date()
      const todayStr = this.formatDate(today)
      if (val > todayStr) {
        this.mobileEndDate = todayStr
        this.$message.warning('结束日期不能选择明天及以后，已自动调整为今天')
      }
      
      this.fetchDashboardData()
    },
    // ZCN ID 输入框失焦
    onZcnCodeBlur() {
      // 只有值变化时才触发查询
      if (this.filters.zcnCode !== this.lastZcnCode) {
        this.lastZcnCode = this.filters.zcnCode
        this.fetchDashboardData()
      }
    },
    // 达成情况表格排序
    sortAchievementData(field) {
      if (this.achievementSort.field === field) {
        this.achievementSort.order = this.achievementSort.order === 'asc' ? 'desc' : 'asc'
      } else {
        this.achievementSort.field = field
        this.achievementSort.order = 'desc'
      }
    },
    // 获取达成情况排序图标
    getAchievementSortIcon(field) {
      if (this.achievementSort.field !== field) return 'el-icon-sort'
      return this.achievementSort.order === 'asc' ? 'el-icon-sort-up' : 'el-icon-sort-down'
    },
    // 医院分析表格排序
    sortHospitalData(field) {
      if (this.hospitalSort.field === field) {
        this.hospitalSort.order = this.hospitalSort.order === 'asc' ? 'desc' : 'asc'
      } else {
        this.hospitalSort.field = field
        this.hospitalSort.order = 'desc'
      }
    },
    // 获取医院分析排序图标
    getHospitalSortIcon(field) {
      if (this.hospitalSort.field !== field) return 'el-icon-sort'
      return this.hospitalSort.order === 'asc' ? 'el-icon-sort-up' : 'el-icon-sort-down'
    },
    // 达成情况图表排序变化
    onAchievementChartSortChange() {
      // 同步更新表格排序
      this.achievementSort.field = this.achievementChartSort.field
      this.achievementSort.order = this.achievementChartSort.order
      this.renderAchievementChart()
    },
    // 切换达成情况图表排序顺序
    toggleAchievementChartOrder() {
      this.achievementChartSort.order = this.achievementChartSort.order === 'asc' ? 'desc' : 'asc'
      // 同步更新表格排序
      this.achievementSort.order = this.achievementChartSort.order
      this.renderAchievementChart()
    },
    // 导出达成情况Excel
    exportAchievementExcel() {
      const data = this.sortedAchievementData.map(item => ({
        '名称': item.name,
        '指标': item.target,
        '上报数量': item.actual,
        '去年同期上报量': item.lastYearActual,
        '达成率(%)': item.achieveRate,
        '增长率(%)': item.growthRate != null ? item.growthRate : '-'
      }))
      
      // 添加汇总行
      data.push({
        '名称': '汇总',
        '指标': this.achievementSummary.target,
        '上报数量': this.achievementSummary.actual,
        '去年同期上报量': this.achievementSummary.lastYearActual,
        '达成率(%)': this.achievementSummary.achieveRate,
        '增长率(%)': this.achievementSummary.growthRate != null ? this.achievementSummary.growthRate : '-'
      })
      
      this.exportToExcel(data, `达成情况_${this.mobileStartDate}_${this.mobileEndDate}`, true)
    },
    // 导出医院分析Excel
    exportHospitalExcel() {
      const data = this.sortedHospitalData.map(item => ({
        '医院名称': item.name,
        'ZCN编码': item.zcnCode,
        '区域': item.regionName || '',
        '姓名': item.salesName || '',
        '指标': item.target,
        '上报数量': item.actual,
        '去年同期上报量': item.lastYearActual,
        '达成率(%)': item.achieveRate,
        '增长率(%)': item.growthRate != null ? item.growthRate : '-'
      }))
      
      // 添加汇总行
      data.push({
        '医院名称': '汇总',
        'ZCN编码': '',
        '区域': '',
        '姓名': '',
        '指标': this.hospitalSummary.target,
        '上报数量': this.hospitalSummary.actual,
        '去年同期上报量': this.hospitalSummary.lastYearActual,
        '达成率(%)': this.hospitalSummary.achieveRate,
        '增长率(%)': this.hospitalSummary.growthRate != null ? this.hospitalSummary.growthRate : '-'
      })
      
      this.exportToExcel(data, `医院分析_${this.mobileStartDate}_${this.mobileEndDate}`, true)
    },
    // 通用导出Excel方法（带样式）
    exportToExcel(data, filename, hasSummary = false) {
      if (!data || data.length === 0) {
        this.$message.warning('没有数据可导出')
        return
      }
      
      const ws = XLSX.utils.json_to_sheet(data)
      const headers = Object.keys(data[0])
      const colCount = headers.length
      const rowCount = data.length + 1 // 包含表头
      
      // 设置列宽（根据表头和数据内容计算最大宽度）
      ws['!cols'] = headers.map((h) => {
        let maxLen = h.length * 2 // 表头长度（中文按2字符算）
        data.forEach(row => {
          const val = String(row[h] || '')
          // 中文字符按2个宽度计算
          const len = val.split('').reduce((acc, char) => acc + (/[\u4e00-\u9fa5]/.test(char) ? 2 : 1), 0)
          if (len > maxLen) maxLen = len
        })
        return { wch: maxLen + 4 } // 加4留边距，不设上限
      })
      
      // 表头样式
      const headerStyle = {
        font: { bold: true, color: { rgb: 'FFFFFF' }, sz: 11 },
        fill: { fgColor: { rgb: '4472C4' } },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: '000000' } },
          bottom: { style: 'thin', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: '000000' } },
          right: { style: 'thin', color: { rgb: '000000' } }
        }
      }
      
      // 数据行样式
      const dataStyle = {
        font: { sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center' },
        border: {
          top: { style: 'thin', color: { rgb: 'D9D9D9' } },
          bottom: { style: 'thin', color: { rgb: 'D9D9D9' } },
          left: { style: 'thin', color: { rgb: 'D9D9D9' } },
          right: { style: 'thin', color: { rgb: 'D9D9D9' } }
        }
      }
      
      // 斑马纹样式
      const altRowStyle = {
        ...dataStyle,
        fill: { fgColor: { rgb: 'F2F2F2' } }
      }
      
      // 汇总行样式
      const summaryStyle = {
        font: { bold: true, sz: 10 },
        alignment: { horizontal: 'center', vertical: 'center' },
        fill: { fgColor: { rgb: 'FFF2CC' } },
        border: {
          top: { style: 'medium', color: { rgb: '000000' } },
          bottom: { style: 'medium', color: { rgb: '000000' } },
          left: { style: 'thin', color: { rgb: 'D9D9D9' } },
          right: { style: 'thin', color: { rgb: 'D9D9D9' } }
        }
      }
      
      // 应用样式到所有单元格
      for (let c = 0; c < colCount; c++) {
        const colLetter = XLSX.utils.encode_col(c)
        // 表头
        const headerCell = `${colLetter}1`
        if (ws[headerCell]) {
          ws[headerCell].s = headerStyle
        }
        // 数据行
        for (let r = 1; r < rowCount; r++) {
          const cellRef = `${colLetter}${r + 1}`
          if (ws[cellRef]) {
            // 如果是最后一行且有汇总，应用汇总样式
            if (hasSummary && r === rowCount - 1) {
              ws[cellRef].s = summaryStyle
            } else {
              ws[cellRef].s = r % 2 === 0 ? altRowStyle : dataStyle
            }
          }
        }
      }
      
      const wb = XLSX.utils.book_new()
      XLSX.utils.book_append_sheet(wb, ws, 'Sheet1')
      XLSX.writeFile(wb, `${filename}.xlsx`)
    },
    // 渲染所有图表
    renderCharts() {
      this.disposeCharts()
      this.renderAchievementChart()
      // this.renderHospitalChart()  // 已删除
      // this.renderWeeklyChart()  // 已删除
      this.renderDailyChart()
    },
    // 达成情况图表
    renderAchievementChart() {
      if (!this.achievementData.length || !this.$refs.achievementChart) return
      this.charts.achievement = echarts.init(this.$refs.achievementChart)
      
      // 根据排序设置对数据排序
      const { field, order } = this.achievementChartSort
      const sortedData = [...this.achievementData].sort((a, b) => {
        let valA = a[field]
        let valB = b[field]
        if (valA == null && valB == null) return 0
        if (valA == null) return 1
        if (valB == null) return -1
        if (typeof valA === 'string') {
          return order === 'asc' ? valA.localeCompare(valB) : valB.localeCompare(valA)
        }
        return order === 'asc' ? valA - valB : valB - valA
      })
      
      const names = sortedData.map(d => d.name)
      const targets = sortedData.map(d => d.target)
      const actuals = sortedData.map(d => d.actual)
      const rates = sortedData.map(d => d.achieveRate)
      const growthRates = sortedData.map(d => d.growthRate)
      const option = {
        tooltip: { 
          trigger: 'axis', 
          axisPointer: { type: 'cross' },
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderColor: '#eee',
          textStyle: { color: '#333' },
          formatter: (params) => {
            const idx = params[0].dataIndex
            let html = `<strong>${params[0].name}</strong><br/>`
            params.forEach(p => {
              if (p.seriesName === '增长率') {
                const grText = p.value == null ? '-' : (p.value > 0 ? '+' + p.value + '%' : p.value + '%')
                html += `${p.marker} ${p.seriesName}: ${grText}<br/>`
              } else {
                html += `${p.marker} ${p.seriesName}: ${p.value}${p.seriesName === '达成率' ? '%' : ''}<br/>`
              }
            })
            return html
          }
        },
        legend: { 
          data: ['指标', '上报数量', '达成率', '增长率'], 
          bottom: 0,
          textStyle: { fontSize: this.isMobile ? 10 : 12 }
        },
        grid: { 
          left: this.isMobile ? 40 : 50, 
          right: this.isMobile ? 40 : 50, 
          top: 40, 
          bottom: this.isMobile ? 50 : 60, 
          containLabel: false 
        },
        xAxis: { 
          type: 'category', 
          data: names, 
          axisLabel: { 
            rotate: this.isMobile ? 60 : 45, 
            fontSize: this.isMobile ? 9 : 10,
            interval: 0,
            formatter: val => val.length > 4 ? val.substring(0, 4) + '..' : val
          }
        },
        yAxis: [
          { type: 'value', name: '上报数量', position: 'left', nameTextStyle: { fontSize: 10 } },
          { type: 'value', name: '%', position: 'right', min: 0, nameTextStyle: { fontSize: 10 } }
        ],
        dataZoom: [
          { type: 'inside', start: 0, end: 100 },
          { type: 'slider', start: 0, end: 100, bottom: 5, height: 20, show: this.isFullscreen }
        ],
        series: [
          { 
            name: '指标', 
            type: 'bar', 
            data: targets, 
            itemStyle: { color: '#e74c3c' }, 
            barMaxWidth: 20,
            label: { show: true, position: 'inside', fontSize: 9, color: '#fff' }
          },
          { 
            name: '上报数量', 
            type: 'bar', 
            data: actuals, 
            itemStyle: { color: '#3498db' }, 
            barMaxWidth: 20,
            label: { show: true, position: 'inside', fontSize: 9, color: '#fff' }
          },
          { 
            name: '达成率', 
            type: 'line', 
            yAxisIndex: 1, 
            data: rates, 
            itemStyle: { color: '#27ae60' }, 
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6,
            label: { 
              show: true, 
              position: 'top',
              formatter: '{c}%', 
              fontSize: 9,
              color: '#27ae60',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: [2, 4],
              borderRadius: 2
            } 
          },
          { 
            name: '增长率', 
            type: 'line', 
            yAxisIndex: 1, 
            data: growthRates, 
            itemStyle: { color: '#f39c12' }, 
            lineStyle: { width: 2, type: 'dashed' },
            symbol: 'diamond',
            symbolSize: 6,
            label: { 
              show: true, 
              position: 'bottom',
              formatter: (params) => {
                if (params.value == null) return '-'
                return (params.value > 0 ? '+' : '') + params.value + '%'
              },
              fontSize: 9,
              color: '#f39c12',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: [2, 4],
              borderRadius: 2
            } 
          }
        ]
      }
      this.charts.achievement.setOption(option)
    },
    // 日期趋势图表 - 折线图 + 平均线
    renderDailyChart() {
      if (!this.dailyTrend.length || !this.$refs.dailyChart) return
      this.charts.daily = echarts.init(this.$refs.dailyChart)
      const dates = this.dailyTrend.map(d => d.date)
      const numbers = this.dailyTrend.map(d => d.number)
      
      // 计算平均值
      const average = this.dailyStatistics.average
      
      const option = {
        tooltip: { 
          trigger: 'axis',
          formatter: (params) => {
            const param = params[0]
            return `${param.name}<br/>上报数量: ${param.value}<br/>平均值: ${average}`
          },
          backgroundColor: 'rgba(255,255,255,0.95)',
          borderColor: '#eee',
          textStyle: { color: '#333' }
        },
        grid: { left: 50, right: 20, top: 30, bottom: 80 },
        xAxis: { 
          type: 'category', 
          data: dates, 
          axisLabel: { 
            rotate: 45, 
            fontSize: 9,
            // 根据数据点数量自动调整间隔：数据多时自动隐藏部分标签
            interval: 'auto',
            formatter: (value) => {
              // 只显示月-日，去掉年份
              return value.substring(5)  // '2026-01-04' -> '01-04'
            }
          }
        },
        yAxis: { 
          type: 'value', 
          name: '上报数量',
          nameLocation: 'middle',
          nameGap: 35,
          nameTextStyle: { fontSize: 11 }
        },
        dataZoom: [
          // 鼠标滚轮缩放
          { 
            type: 'inside', 
            start: dates.length > 30 ? 70 : 0,  // 数据多时默认显示最后30%
            end: 100,
            zoomOnMouseWheel: true,
            moveOnMouseMove: true
          },
          // 滑块（始终显示）
          { 
            type: 'slider', 
            start: dates.length > 30 ? 70 : 0, 
            end: 100, 
            bottom: 5, 
            height: 20,
            show: true,  // 始终显示滑块
            handleSize: '80%',
            textStyle: { fontSize: 10 }
          }
        ],
        series: [
          {
            name: '上报数量',
            type: 'line',  // 改为折线图
            data: numbers,
            smooth: true,  // 平滑曲线
            itemStyle: { color: '#409eff' },
            lineStyle: { width: 2 },
            symbol: 'circle',
            symbolSize: 6,
            areaStyle: {  // 添加面积填充
              color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                { offset: 0, color: 'rgba(64, 158, 255, 0.3)' },
                { offset: 1, color: 'rgba(64, 158, 255, 0.1)' }
              ])
            },
            label: {  // 数据点标签
              show: true,
              position: 'top',
              formatter: '{c}',
              fontSize: 10,
              color: '#409eff',
              backgroundColor: 'rgba(255,255,255,0.8)',
              padding: [2, 6],
              borderRadius: 3
            },
            markLine: {  // 平均线
              silent: true,
              lineStyle: { 
                color: '#f56c6c', 
                type: 'dashed', 
                width: 2 
              },
              label: {
                formatter: `平均: ${average}`,
                position: 'insideStartTop',  // 显示在线的左上方，避免遮挡
                color: '#f56c6c',
                fontSize: 12,
                fontWeight: 'bold',
                backgroundColor: 'rgba(255,255,255,0.95)',
                padding: [4, 8],
                borderRadius: 4,
                borderColor: '#f56c6c',
                borderWidth: 1,
                distance: 5  // 距离线的偏移量
              },
              data: [{ yAxis: average }]
            }
          }
        ]
      }
      this.charts.daily.setOption(option)
    },
    // 调整图表大小
    resizeCharts() {
      Object.values(this.charts).forEach(chart => {
        if (chart) chart.resize()
      })
    },
    // 销毁图表
    disposeCharts() {
      Object.keys(this.charts).forEach(key => {
        if (this.charts[key]) {
          this.charts[key].dispose()
          this.charts[key] = null
        }
      })
    },
    // 切换全屏
    toggleFullscreen(type, element) {
      if (!element) return
      this.fullscreenType = type
      
      if (document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement) {
        // 退出全屏
        if (document.exitFullscreen) {
          document.exitFullscreen()
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen()
        } else if (document.mozCancelFullScreen) {
          document.mozCancelFullScreen()
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen()
        }
      } else {
        // 进入全屏
        if (element.requestFullscreen) {
          element.requestFullscreen()
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen()
        } else if (element.mozRequestFullScreen) {
          element.mozRequestFullScreen()
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen()
        }
      }
    },
    // 全屏状态变化
    onFullscreenChange() {
      const fullscreenElement = document.fullscreenElement || document.webkitFullscreenElement || document.mozFullScreenElement || document.msFullscreenElement
      this.isFullscreen = !!fullscreenElement
      
      // 给全屏元素添加/移除类名（因为 scoped 样式中 :fullscreen 伪类不生效）
      if (fullscreenElement) {
        fullscreenElement.classList.add('is-fullscreen')
      } else {
        // 移除所有卡片的全屏类名
        document.querySelectorAll('.chart-card.is-fullscreen').forEach(el => {
          el.classList.remove('is-fullscreen')
        })
        this.fullscreenType = ''
      }
      
      this.$nextTick(() => {
        // 重新渲染图表以应用新的 dataZoom 配置
        this.renderCharts()
      })
    }
  }
}
</script>


<style scoped>
.flacs-dashboard {
  padding: 16px;
  background: #f5f7fa;
  min-height: 100vh;
}

/* 筛选卡片 */
.filter-card {
  margin-bottom: 16px;
  border-radius: 8px;
}

.filter-toggle {
  text-align: center;
  color: #409eff;
  cursor: pointer;
  padding: 8px 0;
  font-size: 13px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
}

.filter-form {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 8px 10px;
}

.filter-form >>> .el-form-item {
  margin-bottom: 0;
  display: flex;
  align-items: center;
}

.filter-form >>> .el-form-item__label {
  line-height: 32px;
  padding-right: 4px;
  white-space: nowrap;
  font-size: 13px;
}

.filter-form >>> .el-form-item__content {
  line-height: 32px;
}

/* 图表容器 */
.charts-container {
  display: grid;
  grid-template-columns: 1fr;
  gap: 16px;
}

.chart-card {
  background: #fff;
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.06);
  overflow: hidden;
}

.chart-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 12px 16px;
  border-bottom: 1px solid #f0f0f0;
  background: #fafbfc;
}

.chart-title {
  font-weight: 600;
  font-size: 14px;
  color: #303133;
  display: flex;
  align-items: center;
  gap: 6px;
}

.chart-title i {
  color: #409eff;
  font-size: 16px;
}

.chart-title .tip-icon {
  color: #909399;
  font-size: 14px;
  cursor: help;
  margin-left: 2px;
}

.chart-title .tip-icon:hover {
  color: #409eff;
}

.chart-actions {
  display: flex;
  gap: 8px;
  align-items: center;
}

.mobile-sort-bar {
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 8px 12px;
  gap: 8px;
  border-bottom: 1px solid #f0f0f0;
}

.chart-body {
  padding: 12px;
}

.chart-skeleton,
.chart-empty {
  min-height: 290px;
}

.chart-area {
  width: 100%;
  height: 280px;
}

.chart-area-full {
  height: 290px;
}

.chart-table {
  margin-top: 8px;
}

/* 骨架屏 */
.chart-skeleton {
  width: 100%;
  height: 290px;
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  gap: 8px;
  padding: 20px;
}

.skeleton-bar {
  height: 24px;
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

.skeleton-bar.horizontal {
  height: 16px;
}

.chart-skeleton-line {
  background: linear-gradient(90deg, #f0f0f0 25%, #e8e8e8 50%, #f0f0f0 75%);
  background-size: 200% 100%;
  animation: skeleton-loading 1.5s infinite;
  border-radius: 4px;
}

@keyframes skeleton-loading {
  0% { background-position: 200% 0; }
  100% { background-position: -200% 0; }
}

/* 空状态 */
.chart-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  height: 290px;
  color: #c0c4cc;
}

.chart-empty i {
  font-size: 48px;
  margin-bottom: 12px;
}

.chart-empty p {
  font-size: 14px;
  margin: 0;
  color: #909399;
}

.empty-hint {
  font-size: 12px;
  color: #c0c4cc;
  margin-top: 4px;
}

/* 达成率颜色 */
.rate-success { color: #27ae60; font-weight: 600; }
.rate-warning { color: #f39c12; font-weight: 600; }
.rate-danger { color: #e74c3c; font-weight: 600; }

/* 表格折叠区域 */
.table-collapse-wrapper {
  margin-top: 10px;
}

.table-collapse-actions {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.table-collapse-header {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  background: #f5f7fa;
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  color: #606266;
  user-select: none;
}

.table-collapse-header:hover {
  background: #e8eaed;
  color: #409eff;
}

.table-collapse-header i {
  font-size: 12px;
}

/* 转置表格样式 */
.pivot-table-wrapper {
  overflow-x: auto;
  overflow-y: auto;
  max-height: 300px;
  margin-top: 10px;
  max-width: 100%;
  scrollbar-width: thin;
}

.pivot-table-wrapper::-webkit-scrollbar {
  height: 8px;
}

.pivot-table-wrapper::-webkit-scrollbar-track {
  background: #f1f1f1;
  border-radius: 4px;
}

.pivot-table-wrapper::-webkit-scrollbar-thumb {
  background: #c1c1c1;
  border-radius: 4px;
}

.pivot-table-wrapper::-webkit-scrollbar-thumb:hover {
  background: #a1a1a1;
}

/* 全屏时表格滚动 - 已移至 .is-fullscreen 类 */

.pivot-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  table-layout: fixed;
}

.pivot-table th,
.pivot-table td {
  padding: 6px 10px;
  text-align: center;
  border: 1px solid #ebeef5;
  white-space: normal;
  word-break: break-word;
}

.pivot-table th {
  background: #fafafa;
  font-weight: 600;
  color: #606266;
}

/* 可排序表头 */
.sortable-table th.sortable-th {
  cursor: pointer;
  user-select: none;
}

.sortable-table th.sortable-th:hover {
  background: #f0f2f5;
  color: #409eff;
}

.sortable-table th.sortable-th i {
  margin-left: 4px;
  font-size: 12px;
  color: #c0c4cc;
}

.sortable-table th.sortable-th:hover i {
  color: #409eff;
}

.pivot-table .row-header {
  background: #fafafa;
  font-weight: 600;
  color: #606266;
  text-align: left;
  min-width: 70px;
}

.pivot-table tbody tr:hover {
  background: #f5f7fa;
}

/* 全屏样式 - 使用类名而非伪类（scoped 样式中伪类不生效） */
.chart-card.is-fullscreen {
  background: #fff;
  padding: 20px;
  display: flex;
  flex-direction: column;
  overflow: visible;
}

.chart-card.is-fullscreen .chart-header {
  overflow: visible;
  z-index: 10;
  position: relative;
}

.chart-card.is-fullscreen .chart-body {
  flex: 1;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.chart-card.is-fullscreen .chart-content {
  flex: 1;
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
}

.chart-card.is-fullscreen .chart-area {
  flex: 1;
  min-height: 300px;
}

.chart-card.is-fullscreen .chart-area-full {
  flex: 1;
  height: auto !important;
}

.chart-card.is-fullscreen .pivot-table-wrapper {
  flex-shrink: 0;
  overflow-x: auto;
  overflow-y: hidden;
  margin-top: 10px;
}

.chart-card.is-fullscreen .chart-table {
  max-height: none !important;
}

/* 汇总行样式 */
.summary-row {
  background: #f5f7fa !important;
  border-top: 2px solid #409eff;
  font-weight: 600;
}

.summary-row td {
  font-weight: 600;
  color: #303133;
}

.summary-row:hover {
  background: #f5f7fa !important;
}

/* 统计信息栏样式 */
.stats-bar {
  display: flex;
  justify-content: space-around;
  padding: 12px 16px;
  background: #f9fafc;
  border-bottom: 1px solid #ebeef5;
}

.stat-item {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
}

.stat-label {
  font-size: 12px;
  color: #909399;
}

.stat-value {
  font-size: 18px;
  font-weight: 600;
  color: #409eff;
}

/* 分析表格样式 */
.analysis-table-wrapper {
  margin-top: 16px;
}

.table-title {
  margin: 0 0 8px 0;
  font-size: 13px;
  font-weight: 600;
  color: #606266;
  padding-left: 8px;
  border-left: 3px solid #409eff;
}

.analysis-table-scroll {
  overflow-x: auto;
  overflow-y: hidden;
}

.analysis-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 12px;
  background: #fff;
}

.analysis-table th,
.analysis-table td {
  padding: 10px 12px;
  text-align: center;
  border: 1px solid #ebeef5;
  white-space: nowrap;
}

.analysis-table th {
  background: #409eff;
  color: #fff;
  font-weight: 600;
}

.analysis-table tbody tr:hover {
  background: #f5f7fa;
}

.analysis-table tbody tr:nth-child(even) {
  background: #fafbfc;
}

.analysis-table tbody tr:nth-child(even):hover {
  background: #f5f7fa;
}

/* 移动端适配 */
@media (max-width: 767px) {
  .flacs-dashboard {
    padding: 10px;
  }

  .chart-body {
    padding: 10px;
  }

  .chart-area {
    height: 180px;
  }

  .chart-area-full {
    height: 250px;
  }

  .chart-table {
    font-size: 11px;
  }

  .chart-skeleton,
  .chart-empty {
    min-height: 250px;
  }

  .chart-empty i {
    font-size: 40px;
  }

  /* 移动端统计栏 */
  .stat-label {
    font-size: 11px;
  }

  .stat-value {
    font-size: 16px;
  }

  /* 移动端分析表格 */
  .analysis-table-wrapper {
    margin-top: 12px;
  }

  .table-title {
    font-size: 12px;
    margin-bottom: 6px;
  }

  .analysis-table-scroll {
    margin: 0 -10px;
    padding: 0 10px;
  }

  .analysis-table {
    font-size: 11px;
  }

  .analysis-table th,
  .analysis-table td {
    padding: 8px 6px;
    font-size: 11px;
  }

  .filter-form {
    flex-direction: column;
    gap: 0;
  }

  .filter-form .el-form-item {
    margin-bottom: 12px;
    width: 100%;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
  }

  .filter-form >>> .el-form-item__label {
    padding: 0 0 4px 0;
    line-height: 1.2;
    text-align: left;
  }

  .filter-form >>> .el-form-item__content {
    width: 100%;
    margin-left: 0 !important;
  }

  .filter-form >>> .el-cascader,
  .filter-form >>> .el-select,
  .filter-form >>> .el-input,
  .filter-form >>> .el-date-editor {
    width: 100% !important;
  }

  /* 移动端表格横向滚动 */
  .pivot-table-wrapper {
    margin: 8px -12px 0;
    padding: 0 12px;
  }

  .pivot-table {
    font-size: 11px;
  }

  .pivot-table th,
  .pivot-table td {
    padding: 4px 6px;
  }
}

/* 平板适配 */
@media (min-width: 768px) and (max-width: 1024px) {
  .charts-container {
    gap: 12px;
  }
}

/* 表格样式优化 */
.chart-table >>> .el-table__header th {
  background: #fafbfc;
  font-weight: 600;
  font-size: 12px;
}

.chart-table >>> .el-table__row td {
  font-size: 12px;
}

/* 按钮样式 */
.filter-form .el-button {
  margin-left: 0;
}

/* 日期选择器优化 */
.filter-form >>> .el-date-editor .el-range-separator {
  padding: 0 4px;
  line-height: 30px;
}

.filter-form >>> .el-date-editor .el-range-input {
  vertical-align: middle;
}

/* 隐藏日期选择器图标 */
.filter-form >>> .el-date-editor.el-input .el-input__prefix {
  display: none;
}

.filter-form >>> .el-date-editor.el-input .el-input__inner {
  padding-left: 10px;
}

/* 修复多选级联选择器 placeholder 重复显示问题 */
.filter-form >>> .el-cascader .el-cascader__search-input {
  min-width: 10px;
}

.filter-form >>> .el-cascader .el-input__inner::placeholder {
  color: #c0c4cc;
}

/* 隐藏多选级联选择器中重复的搜索输入框 */
.filter-form >>> .el-cascader__tags .el-cascader__search-input:first-child {
  display: none;
}
</style>
